import { useState, useRef, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useBusinessPlan } from '../hooks/useBusinessPlan';
import AnimatedPage from '../components/shared/AnimatedPage';
import LoadingScreen from '../components/shared/LoadingScreen';
import { logoColorToHex } from '../types/businessPlan';

type Tool = 'social' | 'flyer' | 'card';
type SocialLayout = 'quote' | 'spotlight' | 'announcement';

const COLORS = ['#6366f1', '#ef4444', '#f59e0b', '#10b981', '#3b82f6', '#ec4899', '#8b5cf6', '#14b8a6'];

export default function MarketingStudioPage() {
  const { planId } = useParams<{ planId: string }>();
  const navigate = useNavigate();
  const { plan, loading } = useBusinessPlan(planId);

  const [tool, setTool] = useState<Tool>('social');
  const [socialLayout, setSocialLayout] = useState<SocialLayout>('quote');
  const [bgColor, setBgColor] = useState('#6366f1');
  const [editText, setEditText] = useState('');
  const [toastMsg, setToastMsg] = useState<string | null>(null);
  const exportRef = useRef<HTMLDivElement>(null);

  const content = useMemo(() => {
    if (!plan) return null;
    const s1 = plan.stageResponses?.ideaExplosion;
    return {
      name: plan.businessName || 'My Business',
      tagline: plan.elevatorPitchSolution || 'Building something amazing',
      description: s1?.s1_q2 || '',
      logoColor: plan.selectedLogoColors?.[0] ? logoColorToHex(plan.selectedLogoColors[0]) : '#6366f1',
      logoShape: plan.selectedLogoShape || 'square',
    };
  }, [plan]);

  const handleDownload = useCallback(async () => {
    if (!exportRef.current) return;
    try {
      const { default: html2canvas } = await import('html2canvas');
      const canvas = await html2canvas(exportRef.current, { scale: 2, backgroundColor: null });
      const link = document.createElement('a');
      link.download = `${content?.name ?? 'marketing'}-${tool}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    } catch {
      setToastMsg('Download requires html2canvas. Take a screenshot instead!');
      setTimeout(() => setToastMsg(null), 3000);
    }
  }, [tool, content?.name]);

  if (loading) return <LoadingScreen />;
  if (!plan || !content) return (
    <AnimatedPage><div className="flex min-h-screen items-center justify-center"><p className="text-gray-500">Plan not found</p></div></AnimatedPage>
  );

  const displayText = editText || (tool === 'social' ? content.tagline : tool === 'flyer' ? content.description : content.name);

  const tools: { key: Tool; label: string; emoji: string }[] = [
    { key: 'social', label: 'Social Post', emoji: '📱' },
    { key: 'flyer', label: 'Flyer', emoji: '📄' },
    { key: 'card', label: 'Business Card', emoji: '💼' },
  ];

  return (
    <AnimatedPage>
      <div className="min-h-screen bg-gray-50 px-4 pb-12 pt-6">
        <div className="mx-auto max-w-4xl">
          <div className="mb-6 flex items-center justify-between">
            <button onClick={() => navigate(`/hq/${planId}`)} className="text-sm font-medium text-brand-500 hover:text-brand-600">← Back to HQ</button>
            <h1 className="text-lg font-bold text-gray-900">Marketing Studio</h1>
            <button onClick={handleDownload} className="min-h-[44px] rounded-xl bg-brand-500 px-4 py-2 text-sm font-medium text-white hover:bg-brand-600">Download PNG</button>
          </div>

          <div className="mb-6 flex gap-2">
            {tools.map((t) => (
              <button key={t.key} onClick={() => { setTool(t.key); setEditText(''); }} className={`flex min-h-[44px] items-center gap-1 rounded-xl px-4 py-2 text-sm font-medium transition-all ${tool === t.key ? 'bg-brand-500 text-white shadow' : 'bg-white text-gray-600 hover:bg-gray-100'}`}>
                <span>{t.emoji}</span><span className="hidden sm:inline">{t.label}</span>
              </button>
            ))}
          </div>

          <div className="md:flex md:gap-6">
            <div className="mb-6 space-y-4 md:mb-0 md:w-1/2">
              <div className="rounded-xl border border-gray-200 bg-white p-4">
                <label className="mb-1 block text-sm font-medium text-gray-700">Text</label>
                <textarea rows={3} value={editText} onChange={(e) => setEditText(e.target.value)} placeholder={displayText} className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-brand-400 focus:outline-none" />
              </div>
              {tool === 'social' && (
                <div className="rounded-xl border border-gray-200 bg-white p-4">
                  <label className="mb-2 block text-sm font-medium text-gray-700">Layout</label>
                  <div className="flex gap-2">
                    {(['quote', 'spotlight', 'announcement'] as SocialLayout[]).map((l) => (
                      <button key={l} onClick={() => setSocialLayout(l)} className={`min-h-[44px] flex-1 rounded-lg py-2 text-xs font-medium capitalize ${socialLayout === l ? 'bg-brand-500 text-white' : 'bg-gray-100 text-gray-600'}`}>{l}</button>
                    ))}
                  </div>
                </div>
              )}
              <div className="rounded-xl border border-gray-200 bg-white p-4">
                <label className="mb-2 block text-sm font-medium text-gray-700">Color</label>
                <div className="flex gap-2">
                  {COLORS.map((c) => (
                    <button key={c} onClick={() => setBgColor(c)} className={`h-8 w-8 rounded-full ${bgColor === c ? 'ring-2 ring-brand-500 ring-offset-2' : ''}`} style={{ backgroundColor: c }} />
                  ))}
                </div>
              </div>
            </div>

            <div className="md:w-1/2">
              <div ref={exportRef}>
                {tool === 'social' && (
                  <div className="mx-auto aspect-square max-w-sm overflow-hidden rounded-2xl text-white" style={{ backgroundColor: bgColor }}>
                    {socialLayout === 'quote' && (
                      <div className="flex h-full flex-col items-center justify-center p-8 text-center">
                        <span className="text-4xl">&ldquo;</span>
                        <p className="mt-2 text-lg font-bold leading-tight">{displayText}</p>
                        <p className="mt-4 text-sm opacity-80">— {content.name}</p>
                      </div>
                    )}
                    {socialLayout === 'spotlight' && (
                      <div className="flex h-full flex-col justify-between p-8">
                        <div className="flex h-20 w-20 items-center justify-center rounded-2xl bg-white/20 text-3xl" style={{ color: bgColor }}>{content.logoShape === 'circle' ? '●' : '★'}</div>
                        <div><p className="text-xl font-bold">{content.name}</p><p className="mt-2 text-sm opacity-90">{displayText}</p></div>
                      </div>
                    )}
                    {socialLayout === 'announcement' && (
                      <div className="flex h-full flex-col items-center justify-center p-8 text-center">
                        <p className="text-xs font-medium uppercase tracking-widest opacity-70">Introducing</p>
                        <p className="mt-3 text-2xl font-bold">{content.name}</p>
                        <div className="mt-4 h-px w-12 bg-white/40" />
                        <p className="mt-4 text-sm opacity-90">{displayText}</p>
                      </div>
                    )}
                  </div>
                )}
                {tool === 'flyer' && (
                  <div className="mx-auto max-w-sm overflow-hidden rounded-2xl text-white" style={{ backgroundColor: bgColor, aspectRatio: '3/4' }}>
                    <div className="flex h-full flex-col justify-between p-8">
                      <div><p className="text-2xl font-bold">{content.name}</p><p className="mt-1 text-sm opacity-80">{content.tagline}</p></div>
                      <div className="rounded-xl bg-white/15 p-4 backdrop-blur-sm"><p className="text-sm leading-relaxed">{displayText}</p></div>
                      <p className="text-center text-xs opacity-60">Learn more at mybusiness.com</p>
                    </div>
                  </div>
                )}
                {tool === 'card' && (
                  <div className="mx-auto max-w-sm space-y-4">
                    <div className="overflow-hidden rounded-xl text-white" style={{ backgroundColor: bgColor, aspectRatio: '1.75/1' }}>
                      <div className="flex h-full items-center justify-between p-6">
                        <div><p className="text-lg font-bold">{content.name}</p><p className="mt-1 text-xs opacity-80">{displayText || content.tagline}</p></div>
                        <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-white/20 text-xl" style={{ color: bgColor }}>{content.logoShape === 'circle' ? '●' : content.logoShape === 'star' ? '★' : '■'}</div>
                      </div>
                    </div>
                    <div className="overflow-hidden rounded-xl bg-white" style={{ aspectRatio: '1.75/1', border: `2px solid ${bgColor}` }}>
                      <div className="flex h-full flex-col items-center justify-center p-6 text-center">
                        <p className="text-sm font-bold" style={{ color: bgColor }}>{content.name}</p>
                        <p className="mt-2 text-xs text-gray-500">hello@mybusiness.com</p>
                        <p className="text-xs text-gray-400">mybusiness.com</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {toastMsg && (
          <motion.div initial={{ opacity: 0, y: 50 }} animate={{ opacity: 1, y: 0 }} className="fixed bottom-6 left-1/2 z-50 -translate-x-1/2 rounded-xl bg-gray-900 px-6 py-3 text-sm font-medium text-white shadow-lg">{toastMsg}</motion.div>
        )}
      </div>
    </AnimatedPage>
  );
}
